const transferSteps = [
  {
    icon: "ri-hotel-line",
    label: "Step 1",
    heading: "Arrange your water taxi transfer when you book",
    body: "Simply let the Bedarra Island Resort team know the night before. They'll organise the water taxi transfer to Mission Beach for you — no stress, no guesswork.",
  },
  {
    icon: "ri-ship-line",
    label: "Step 2",
    heading: "20-minute water taxi to Clump Point",
    body: "The Bedarra water taxi takes you straight to Clump Point Jetty — no extra transfers, no detours. A scenic, easy crossing, and the cost is included in your package price.",
  },
  {
    icon: "ri-map-pin-2-line",
    label: "Step 3",
    heading: "Meet the crew & grab your gear",
    body: "Step off the water taxi and you're already at Clump Point Jetty. Arrive 30 minutes before your 7:30 am departure — the crew will be there with your gear ready to go.",
  },
  {
    icon: "ri-anchor-line",
    label: "Step 4",
    heading: "Board MV Mahi-Mahi & head to the reef",
    body: "Meet your crew, get your briefing, and you're off. The outer Great Barrier Reef is 45–60 minutes offshore. Your day on the water begins.",
  },
];

const packages = [
  {
    icon: "ri-bubble-chart-line",
    tag: "Certified Divers",
    name: "Certified Diver Day Trip",
    price: "$799",
    color: "from-cyan-500 to-teal-500",
    bgLight: "bg-cyan-50",
    textAccent: "text-cyan-600",
    borderAccent: "border-cyan-200",
    includes: [
      "Water taxi transfer from Bedarra Island",
      "Return boat trip aboard MV Mahi-Mahi",
      "2 guided dives on the outer reef",
      "All scuba equipment & tanks",
      "PADI-certified dive guide",
      "Reef entry fees & GST",
    ],
    cta: "Book Certified Diver Trip",
  },
  {
    icon: "ri-water-flash-line",
    tag: "First-Timers",
    name: "Discover Scuba Diving",
    price: "$699",
    color: "from-orange-500 to-amber-500",
    bgLight: "bg-orange-50",
    textAccent: "text-orange-600",
    borderAccent: "border-orange-200",
    featured: true,
    includes: [
      "Water taxi transfer from Bedarra Island",
      "Return boat trip aboard MV Mahi-Mahi",
      "PADI Discover Scuba Diving program",
      "1-on-1 PADI instructor in the water",
      "All equipment provided",
      "Reef entry fees & GST",
    ],
    cta: "Book Discover Scuba",
  },
  {
    icon: "ri-sun-line",
    tag: "Non-Divers",
    name: "Snorkel Day Trip",
    price: "$459",
    color: "from-emerald-500 to-teal-400",
    bgLight: "bg-emerald-50",
    textAccent: "text-emerald-600",
    borderAccent: "border-emerald-200",
    includes: [
      "Water taxi transfer from Bedarra Island",
      "Return boat trip aboard MV Mahi-Mahi",
      "Guided snorkel session on the reef",
      "Mask, fins & snorkel provided",
      "Reef entry fees & GST",
    ],
    cta: "Book Snorkel Trip",
  },
];

export default function Logistics() {
  return (
    <section id="logistics" className="w-full bg-white py-24 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">

        {/* ── Section Header ── */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[3px] text-cyan-500 font-semibold mb-4">
            Getting There
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-5">
            From Bedarra Island to the Reef —<br className="hidden md:block" />
            <span className="text-cyan-500">Easier Than You Think</span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto font-light leading-relaxed">
            The water taxi transfer from Bedarra Island is included in every package price.
            Just tell the resort, show up, and we handle everything else.
          </p>
        </div>

        {/* ── Transfer Steps ── */}
        <div className="relative mb-20">
          {/* Connecting line — desktop only */}
          <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-cyan-100 z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {transferSteps.map((step, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                {/* Icon circle */}
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-cyan-50 border-2 border-cyan-100 text-cyan-500 mb-5 shrink-0">
                  <i className={`${step.icon} text-2xl`} />
                </div>
                <span className="text-[10px] uppercase tracking-[3px] text-cyan-400 font-semibold mb-1">
                  {step.label}
                </span>
                <h3 className="text-sm font-bold text-gray-900 mb-2 leading-snug px-2">
                  {step.heading}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed px-2">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Clump Point Jetty Map ── */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 flex items-center justify-center text-cyan-500">
              <i className="ri-map-pin-2-fill text-lg" />
            </div>
            <p className="text-sm font-semibold text-gray-700">
              Your departure point — <span className="text-cyan-600">Clump Point Jetty, Mission Beach</span>
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-cyan-100" style={{ height: "280px" }}>
            <iframe
              title="Clump Point Jetty, Mission Beach"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3779.8523!2d146.1017!3d-17.8697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6980b3a5e5e5e5e5%3A0xabcdef1234567890!2sClump%20Point%20Jetty%2C%20Mission%20Beach%20QLD%204852%2C%20Australia!5e0!3m2!1sen!2sau!4v1712600000000!5m2!1sen!2sau"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            The water taxi drops you directly at Clump Point Jetty — where MV Mahi-Mahi departs for the reef.
          </p>
        </div>

        {/* ── Transfer Included Banner ── */}
        <div className="flex items-center gap-4 bg-cyan-50 border border-cyan-100 rounded-2xl px-6 py-5 mb-20">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-100 text-cyan-600 shrink-0">
            <i className="ri-checkbox-circle-fill text-xl" />
          </div>
          <div>
            <p className="text-cyan-800 font-bold text-sm">Water taxi transfer included in all packages</p>
            <p className="text-cyan-600 text-xs mt-0.5 leading-relaxed">
              The Bedarra Island to Clump Point Jetty water taxi is covered in your package price — no hidden extras, no separate booking needed.
            </p>
          </div>
        </div>

        {/* ── Pricing Cards ── */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[3px] text-cyan-500 font-semibold mb-4">
            Pricing
          </p>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
            All-Inclusive Package Prices
          </h2>
          <p className="text-gray-400 text-sm mt-3">
            Transfer, boat, gear, guide &amp; reef fees — everything included. No surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.name}
              className={`relative flex flex-col rounded-2xl border-2 ${pkg.borderAccent} overflow-hidden ${pkg.featured ? "ring-2 ring-orange-400 ring-offset-2" : ""}`}
            >
              {pkg.featured && (
                <div className="absolute top-0 left-0 right-0 text-center py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] font-bold uppercase tracking-widest">
                  Most Popular
                </div>
              )}

              <div className={`p-7 ${pkg.featured ? "pt-10" : ""}`}>
                {/* Icon + tag */}
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full ${pkg.bgLight} ${pkg.textAccent}`}>
                    <i className={`${pkg.icon} text-xl`} />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-widest ${pkg.textAccent}`}>
                    {pkg.tag}
                  </span>
                </div>

                {/* Name + price */}
                <h3 className="text-lg font-black text-gray-900 mb-1 leading-snug">
                  {pkg.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className={`text-4xl font-black ${pkg.textAccent}`}>{pkg.price}</span>
                  <span className="text-gray-400 text-sm font-light">AUD / person</span>
                </div>

                {/* Includes list */}
                <ul className="flex flex-col gap-2.5 mb-8">
                  {pkg.includes.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <div className={`w-4 h-4 flex items-center justify-center shrink-0 mt-0.5 ${pkg.textAccent}`}>
                        <i className="ri-check-line text-sm" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`whitespace-nowrap w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm cursor-pointer hover:scale-105 transition-transform duration-300 text-white bg-gradient-to-r ${pkg.color}`}
                  style={{ boxShadow: pkg.featured ? "0 8px 24px rgba(249,115,22,0.35)" : undefined }}
                >
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                  {pkg.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Fine print */}
        <p className="text-center text-gray-400 text-xs mt-8 leading-relaxed">
          All prices in AUD and include GST, reef entry fees, and Bedarra Island water taxi transfer.
          Prices subject to change — contact us to confirm availability and current rates.
        </p>

      </div>
    </section>
  );
}
